package uk.co.nickthecoder.kyd.shapes

import uk.co.nickthecoder.glok.property.boilerplate.floatProperty
import uk.co.nickthecoder.glok.property.invalidationListener
import uk.co.nickthecoder.glok.property.property
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.util.Matrix
import uk.co.nickthecoder.glok.util.log
import uk.co.nickthecoder.kyd.appearance.Appearance
import uk.co.nickthecoder.kyd.util.Vector2

/**
 * Draws a [Shape] (such as a [Line] or [Polygon]).
 *
 * Shapes are drawn twice, once to _fill_ then and once to _stroke_ them.
 * The _stroke_ is an outline of the shape.
 * Unlike most drawing software, this is true for [Line] too!
 * In this case the "main" part of the line is _filled_ using [lineWidth] to determine the thickness.
 * A _stroke_ then uses [strokeWidth] and [strokeRatio].
 * For example, here is a horizontal line. The gaps between the double-lines is the stroke and is defined
 * by [strokeWidth] whereas the main thickness is defined by [lineWidth].
 *
 *         ╔══════════════════════════════════════╗  ▲
 *         ║                                      ║  │
 *         ║                                      ║  │ lineWidth
 *         ║                                      ║  │
 *         ╚══════════════════════════════════════╝  ▼
 *
 * NOTE. The meshes are relatively slow to calculate, but in most cases the meshes are calculated only once.
 * (Unless the shape's geometry changes or [strokeWidth], [strokeRatio] or [lineWidth] are changed.
 */
class ShapeAppearance(shape: Shape) : Appearance {

    val shapeProperty by property<Shape>(shape)
    var shape by shapeProperty

    val fillProperty by property<Paint>(FlatPaint(Color.WHITE))
    var fill by fillProperty

    val strokeProperty by property<Paint>(if (shape is Line) NoPaint else FlatPaint(Color.BLACK))
    var stroke by strokeProperty


    val strokeWidthProperty by floatProperty(if (shape is Line) 0f else 1f)
    var strokeWidth by strokeWidthProperty

    /**
     * How exterior is the stroke to the shape, in the range 0..1
     *
     * * 0 : Stroke is interior to the shape.
     * * 0.5 : Stroke is equally interior and exterior to the shape.
     * * 1 : Stroke is exterior to the shape.
     *
     * default = 0.5
     */
    val strokeRatioProperty by floatProperty(0.5f)
    var strokeRatio by strokeRatioProperty

    /**
     * Only used for lines
     */
    val lineWidthProperty by floatProperty(1f)
    var lineWidth by lineWidthProperty

    val capStyleProperty by property<CapStyle>(CapStyle.ROUND)
    var capStyle by capStyleProperty

    val joinStyleProperty by property<JoinStyle>(JoinStyle.ROUND)
    var joinStyle by joinStyleProperty


    private var cachedFillMesh: Mesh? = null
    private var cachedStrokeMesh: Mesh? = null

    val fillMesh: Mesh
        get() {
            var mesh = cachedFillMesh
            if (mesh == null) {
                mesh = shape.createFillMesh(this)
                cachedFillMesh = mesh
            }
            return mesh
        }

    val strokeMesh: Mesh
        get() {
            var mesh = cachedStrokeMesh
            if (mesh == null) {
                mesh = shape.createStrokeMesh(this)
                cachedStrokeMesh = mesh
            }
            return mesh
        }

    private val invalidateListener = invalidationListener { invalidate() }

    init {
        for (prop in listOf(
            shape.updateCounterProperty, fillProperty, strokeProperty,
            strokeWidthProperty, strokeRatioProperty, lineWidthProperty,
            capStyleProperty, joinStyleProperty
        )) {
            prop.addListener(invalidateListener)
        }
        shapeProperty.addChangeListener { _, old, new ->
            old.updateCounterProperty.removeListener(invalidateListener)
            new.updateCounterProperty.addListener(invalidateListener)
            invalidate()
        }
    }

    protected fun invalidate() {
        cachedFillMesh?.destroy()
        cachedStrokeMesh?.destroy()
        cachedFillMesh = null
        cachedStrokeMesh = null
    }

    override fun localBounds(): Pair<Vector2, Vector2> {
        val (min, max) = shape.bounds()
        val margin = if (shape is Line) {
            lineWidth / 2 + strokeWidth * strokeRatio
        } else {
            strokeWidth * strokeRatio
        }

        return Pair(min.minus(margin, margin), max.plus(margin, margin))
    }

    override fun localCorners(): List<Vector2> {
        val shape = shape
        if (shape is SimpleShape) {
            return shape.points
        } else {
            val (a, b) = localBounds()
            return listOf(a, b)
        }
    }

    fun movePoint(index: Int, newPoint: Vector2) {
        shape.movePoint(index, newPoint)
    }

    fun deletePoint(index: Int) {
        shape.deletePoint(index)
    }

    fun addPoint(index: Int) {
        shape.addPoint(index)
    }

    override fun containsLocalPoint(localPoint: Vector2): Boolean {
        val (min, max) = localBounds()
        if (localPoint.x < min.x) return false
        if (localPoint.x > max.x) return false
        if (localPoint.y < min.y) return false
        return (localPoint.y <= max.y)
    }

    override fun draw(modelMatrix: Matrix) {
        if (fill !== NoPaint) {
            fill.draw(fillMesh, modelMatrix)
        }
        if (stroke !== NoPaint && strokeWidth > 0) {
            stroke.draw(strokeMesh, modelMatrix)
        }
    }

    override fun toString() =
        "ShapeAppearance fill : $fill stroke $stroke : $strokeWidth : $strokeRatio lineWidth : $lineWidth"
}
